import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { DataGrid, ptBR } from '@mui/x-data-grid';
import { FilterAlt, Home as HomeIcon } from '@mui/icons-material';
import { WorkerIcon } from '../../../common/Icons';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { TooltipSelectDisabled } from '../../../common/TooltipSelectDisabled';
import { useAppContext } from '../../../../contexts/AppContext';
import { checkPermissionsAndRedirect, getFormErrorMessage, verifyFieldsAreFilled } from '../../../../configs/functions';
import { listaEmpresas } from '../../management/companies/configs/functions';
import { exportSaidasProdutosPorFuncToPDF } from './configs/utils';
import { getSaidasProdutosFuncAgrupadoData, getSaidasProdutosFuncData } from './configs/functions';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { listaUsuariosFilter } from '../../management/people/configs/functions';
import { CustomToolbarReports } from '../../../common/CustomToolbarReports';
import { PaginatedUserAutocomplete } from '../../../common/PaginatedUserAutocomplete';
import DateRangePicker from '../../../common/DateRangePicker';
import { formatDateTime, parseDateTime } from '../../../../utils/format-date';

export function SaidaProdutosFuncionario() {
  const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();
  const { dadosUsuario, getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

  // estados para controle de paginacao
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(20);
  const [numTotalItems, setNumTotalItems] = useState(0);

  const [dateRange, setDateRange] = useState([null, null]);
  const [dataSaidaProdutos, setDataSaidaProdutos] = useState([]);
  const [isDaily, setIsDaily] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);

  const [pessoas, setPessoas] = useState([]);
  const [pagePessoas, setPagePessoas] = useState(0);
  const [isLoadingPessoas, setIsLoadingPessoas] = useState(false);
  const [hasMorePessoas, setHasMorePessoas] = useState(true);

  const {
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const columns = useMemo(() => {
    const baseColumns = [
      { field: 'nomeResponsavel', headerName: 'Responsável', minWidth: 150, flex: 1 },
      { field: 'produtoCa', headerName: 'CA', minWidth: 100, flex: 0.5 },
      { field: 'produtoNome', headerName: 'Produto', minWidth: 330, flex: 1.5 },
      { field: 'produtoCodExterno', headerName: 'Cód. Produto', minWidth: 120, flex: 0.5 },
      { field: 'quantidade', headerName: 'Quantidade', minWidth: 150, flex: 0.5 },
    ];

    if (isDaily) {
      baseColumns.push({
        field: 'dataSaida',
        headerName: 'Data Saída',
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => parseDateTime(params.row?.dataSaida, "DD-MM-YYYY"),
        renderCell: (params) => formatDateTime(params.row?.dataSaida, "DD/MM/YYYY", "DD-MM-YYYY"),
      });
    } else {
      baseColumns.push({ field: 'numeroSaidas', headerName: 'Saídas', minWidth: 200, flex: 0.5 });
    }

    setNumTotalItems(0);
    setDataSaidaProdutos([]);

    return baseColumns;
  }, [isDaily]);

  const { data: empresasData, error: listaEmpresasError, isLoading: isLoadingEmpresasData, isPending } = useQuery({
    queryKey: ['listaEmpresas'],
    queryFn: async () => {
      const response = await listaEmpresas(null, null, null, true);
      const empresasData = response?.data.data;

      let defaultEmpresaId = null;
      const selectedEmpresaIdSession = getEmpresaIdSession();
      if (selectedEmpresaIdSession) {
        defaultEmpresaId = selectedEmpresaIdSession;
      } else {
        defaultEmpresaId = empresasData[0].id;
        setEmpresaIdSession(empresasData[0].id);
      }

      setSelectedEmpresaId(defaultEmpresaId);

      const defaultValues = {
        id_empresa: defaultEmpresaId,
        id_responsavel_entrega: '',
        data_inicio: dayjs().subtract(1, 'month'),
        data_fim: dayjs(),
      };
      reset(defaultValues);

      return response;
    },
    refetchOnWindowFocus: false,
  });

  const empresas = empresasData?.data.data || [];
  const isLoadingEmpresas = isLoadingEmpresasData || isPending;

  async function loadReportData(id_empresa, id_responsavel_entrega, data_inicio, data_fim) {
    try {
      setIsLoading(true);

      data_inicio = dayjs(data_inicio).format('YYYY-MM-DD');
      data_fim = dayjs(data_fim).format('YYYY-MM-DD');

      const filter = {
        id_empresa,
        data_inicio,
        data_fim,
        diario: isDaily,
        // limit: rowsPerPage,
        // offset: page * rowsPerPage,
      };

      if (id_responsavel_entrega !== 'all') {
        filter.id_responsavel_entrega = id_responsavel_entrega
      }

      const res = await getSaidasProdutosFuncData(filter);

      setDataSaidaProdutos(res.data.saidas);
      setNumTotalItems(parseInt(res.data.numero_total));
    } catch (error) {
      console.error(error);
      exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error')
    } finally {
      setIsLoading(false);
    }
  };

  async function loadExportPdfData(id_empresa, id_responsavel_entrega, data_inicio, data_fim) {
    try {
      data_inicio = dayjs(data_inicio).format('YYYY-MM-DD');
      data_fim = dayjs(data_fim).format('YYYY-MM-DD');

      const filter = {
        id_empresa,
        data_inicio,
        data_fim,
        diario: isDaily,
      };

      if (id_responsavel_entrega !== 'all') {
        filter.id_responsavel_entrega = id_responsavel_entrega
      }

      const res = await getSaidasProdutosFuncAgrupadoData(filter);
      return res.data.saidas;
    } catch (error) {
      console.error(error);
      exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error')
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSearched(true);
      loadReportData(data.id_empresa, data.id_responsavel_entrega, data.data_inicio, data.data_fim);
    } catch (error) {
      exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error');
      setIsSearched(false);
    }
  };

  useEffect(() => {
    const requiredPermissionsView = ["admin", "admin_relatorio"];
    checkPermissionsAndRedirect(requiredPermissionsView);

    return () => {
      reset();
    }
  }, []);

  const listaColaboradores = async (currentPage = 0, initialPageValue = false, filterValue = "") => {
    if (!selectedEmpresaId) return;

    if (initialPageValue) {
      setPessoas([]);
      setPagePessoas(0);
      setHasMorePessoas(true);
    }

    try {
      setIsLoadingPessoas(true);

      const filters = {
        id_empresa: selectedEmpresaId,
        limit: 10,
        offset: (initialPageValue ? 0 : currentPage) * 10,
      };

      // if nome is only number, then it's matricula
      if (filterValue && !isNaN(filterValue)) {
        filters.matricula = filterValue;
        delete filters.nome;
      } else {
        filters.nome = filterValue;
        delete filters.matricula;
      }

      filters.isFilter = true;
      const response = await listaUsuariosFilter(filters);
      const newOptions = response.data.data;

      setPessoas((prevOptions) => [...prevOptions, ...newOptions]);
      setHasMorePessoas(newOptions.length > 0);
      setPagePessoas((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Erro ao carregar colaboradores:", error);
    } finally {
      setIsLoadingPessoas(false);
    }
  }

  useEffect(() => {
    listaColaboradores(0, true);
  }, [selectedEmpresaId]);

  useEffect(() => {
    const id_empresa = selectedEmpresaId;
    const id_responsavel_entrega = getValues('id_responsavel_entrega');
    const data_inicio = getValues('data_inicio')
    const data_fim = getValues('data_fim')

    if (!id_responsavel_entrega || !data_inicio || !data_fim) {
      return;
    }

    loadReportData(id_empresa, id_responsavel_entrega, data_inicio, data_fim);
  }, []);

  useEffect(() => {
    if (listaEmpresasError) {
      exibirAlerta('Ops', 'Ocorreu um erro ao carregar dados das empresas', 'error');
    }
  }, [exibirAlerta, listaEmpresasError])

  async function exportDataToPDF(visibleColumns, reportName) {
    if (!getValues('id_responsavel_entrega')) {
      return exibirAlerta('Ops', 'Selecione um colaborador para exportar', 'warning');
    }

    const dataInicio = getValues('data_inicio');
    const dataFim = getValues('data_fim');

    // if date range is not selected
    if (!dataInicio || !dataFim) {
      return exibirAlerta('Ops', 'Selecione um período para exportar', 'warning');
    }

    try {
      exibirDialog('Exportando dados para PDF, aguarde...');

      // get data grouped by responsavel to export in pdf 
      const tableData = await loadExportPdfData(selectedEmpresaId, getValues('id_responsavel_entrega'), dataInicio, dataFim);

      exportSaidasProdutosPorFuncToPDF({
        colaborador: pessoas.find(pessoa => pessoa.id == getValues('id_responsavel_entrega')),
        tableData,
        reportName,
        dataInicio: dayjs(dataInicio).format('DD/MM/YYYY'),
        dataFim: dayjs(dataFim).format('DD/MM/YYYY'),
        isDaily,
        selectedEmpresa: empresas.find(empresa => empresa.id == selectedEmpresaId),
        dadosUsuario
      })
    } catch (error) {
      exibirAlerta('Erro', 'Erro ao exportar dados para PDF', 'error');
      console.log(error);
    } finally {
      handleCloseDialog();
    }
  }

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="stretch"
      spacing={3}
    >
      <Grid item xs={12}>
        <Breadcrumbs>
          <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
          <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao>
          <LinkNavegacao to='/epis/relatorios'>Relatórios</LinkNavegacao>
          <Typography variant='span'>Relatório de Entregas de Produto por Responsável</Typography>
        </Breadcrumbs>
        <PageTitle icon={<WorkerIcon fontSize='large' />} title="Relatório de Entregas de Produto por Responsável" />
      </Grid>

      <Grid container item xs={12} spacing={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container item xs={12} md={12} spacing={3}>
          <Grid item xs={12} md={6}>
            <SelectEmpresaFilter
              empresas={empresas}
              id_empresas={empresas}
              defaultValue={selectedEmpresaId}
              onChangeValue={(value) => {
                setIsSearched(false);
                setValue('id_empresa', value, { shouldDirty: true });
                setSelectedEmpresaId(value);
                setEmpresaIdSession(value);
                setValue('id_responsavel_entrega', null, { shouldDirty: true })
              }}
              textHelper={false}
              isLoading={isLoadingEmpresas}
            />
          </Grid>

          {pessoas &&
            <Grid item xs={12} md={6}>
              <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                <PaginatedUserAutocomplete
                  name="id_responsavel_entrega"
                  control={control}
                  rules={{ required: true }}
                  label="Responsável Entrega"
                  disabled={!selectedEmpresaId}
                  options={pessoas.length > 0 ? [{ id: 'all', matricula: '', nome: 'Todos', status: 1 }, ...pessoas] : []}
                  getOptionLabel={(option) => {
                    const pessoa = pessoas.find(e => e.id === option);
                    return option === 'all' ? 'Todos' : pessoa ? `${pessoa.matricula} - ${pessoa.nome}` : '';
                  }}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.id} disabled={option.status === 0}>
                      {option.id !== 'all' && `${option.matricula} -`} {option.nome} {option.status === 0 ? '- (Inativo)' : ''}
                    </MenuItem>
                  )}
                  onChange={(event, value) => {
                    setIsSearched(false);
                    setValue('id_responsavel_entrega', value ? value.id : null, { shouldDirty: true });
                    if (value) {
                      clearErrors('id_responsavel_entrega');
                    } else {
                      listaColaboradores(0, true);
                    }
                  }}
                  onInput={(event) => {
                    setPessoas([]);
                    setPagePessoas(0);
                    setHasMorePessoas(true);
                    const filterValue = event.target.value ?? "";
                    listaColaboradores(0, true, filterValue);
                  }}
                  hasMore={hasMorePessoas}
                  loadOptionsFn={() => listaColaboradores(pagePessoas, false, "")}
                  helperText={getFormErrorMessage(errors, 'id_responsavel_entrega')}
                  error={!!errors.id_responsavel_entrega}
                  loading={isLoadingPessoas}
                  textFieldSize="small"
                  textFieldStyle={{ backgroundColor: '#fff' }}
                />
              </TooltipSelectDisabled>
            </Grid>
          }
        </Grid>

        <Grid container item xs={12} md={12} spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <DateRangePicker
              value={dateRange}
              bgColor='#fff'
              onChange={(value) => {
                setDateRange(value);
                const [start, end] = value;
                setValue('data_inicio', start ? dayjs(start).format('YYYY-MM-DD') : '');
                setValue('data_fim', end ? dayjs(end).format('YYYY-MM-DD') : '');
                setIsSearched(false);
              }}
              onReset={() => {
                setDateRange([null, null]);
                setValue('data_inicio', '');
                setValue('data_fim', '');
                setIsSearched(false);
              }}
              hasError={errors.data_inicio || errors.data_fim}
            />
          </Grid>

          <Grid item xs={8} md={2.5}>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDaily}
                    onChange={() => setIsDaily(prev => !prev)}
                    color="primary"
                  />
                }
                label="Separar por dia"
              />
            </Stack>
          </Grid>

          <Grid item xs={4} md={1}>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              disabled={isLoading || Object.keys(errors).length > 0 || !verifyFieldsAreFilled([getValues('id_responsavel_entrega'), getValues('data_inicio'), getValues('data_fim')])}
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "textSecondary" }} /> : <FilterAlt />}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{
          height: dataSaidaProdutos.length > 0 ? "auto" : 400,
          minHeight: 400,
          width: '100%',
          '& .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer': {
            backgroundColor: '#d8d8d8',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderRadius: 0
          },
        }}>
          <DataGrid
            getRowId={(row) => `${row.idRetirada}`}
            rows={dataSaidaProdutos}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 20, 50, 100]}
            rowCount={numTotalItems}
            paginationMode="client"
            // paginationModel={{ pageSize: rowsPerPage, page: page }}
            // onPaginationModelChange={({ page, pageSize }) => {
            //   setPage(page);
            //   setRowsPerPage(pageSize);
            // }}
            sx={{
              backgroundColor: '#fff',
              '.MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold !important',
                overflow: 'visible !important',
              }
            }}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            slots={{
              toolbar: (props) =>
                <CustomToolbarReports
                  reportName="relatorio_saidas_produtos_funcionario"
                  pdfExport={exportDataToPDF}
                  props={props}
                />,
              loadingOverlay: LinearProgress,
              noRowsOverlay: () => (
                <Box style={{ display: "flex", width: '100%', textAlign: 'center', height: "100%", alignItems: 'center', justifyContent: 'center' }}>
                  {isSearched ?
                    <Typography variant='h6' color='textSecondary'>Nenhum registro encontrado para o filtro selecionado</Typography>
                    :
                    <Typography variant='h6' color='textSecondary'>Selecione um colaborador e o período que deseja filtrar</Typography>
                  }
                </Box>
              )
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}